/**
 * Shared event routing logic for observability handlers (exporters and bridges).
 *
 * Both ObservabilityExporter and ObservabilityBridge implement the same optional
 * signal handlers (onTracingEvent, onLogEvent, onMetricEvent, onScoreEvent,
 * onFeedbackEvent). This module provides a single routing function used by both
 * the ObservabilityBus (for exporters) and BaseObservabilityInstance (for bridges).
 */

import type { IMastraLogger } from '@mastra/core/logger';
import type {
  ObservabilityEvents,
  TracingEvent,
  LogEvent,
  MetricEvent,
  ScoreEvent,
  FeedbackEvent,
  ObservabilityEvent,
  ObservabilityDropEvent,
} from '@mastra/core/observability';
import { TracingEventType } from '@mastra/core/observability';

/**
 * Any handler that can receive routed observability events.
 * Both ObservabilityExporter and ObservabilityBridge extend
 * ObservabilityEvents, so this matches either.
 */
export type ObservabilityHandler = ObservabilityEvents & { name: string };

type LegacyScoreHandler = ObservabilityHandler & {
  addScoreToTrace?: (args: {
    traceId: string;
    spanId?: string;
    score: number;
    reason?: string;
    scorerName: string;
    metadata?: Record<string, any>;
  }) => void | Promise<void>;
};

/**
 * Route a single event to the appropriate method on a handler.
 *
 * For tracing events, prefers onTracingEvent when present and falls back
 * to exportTracingEvent. For all other signals, calls the corresponding
 * optional handler method if the handler implements it.
 *
 * Returns the handler promise (if async) so callers can track it for flush().
 * Async rejections are caught to prevent unhandled rejections.
 * Sync throws are caught so one failing handler doesn't break others.
 */
export function routeToHandler(
  handler: ObservabilityHandler,
  event: ObservabilityEvent,
  logger: IMastraLogger,
): void | Promise<void> {
  try {
    switch (event.type) {
      case TracingEventType.SPAN_STARTED:
      case TracingEventType.SPAN_UPDATED:
      case TracingEventType.SPAN_ENDED: {
        const fn = handler.onTracingEvent
          ? handler.onTracingEvent.bind(handler)
          : handler.exportTracingEvent.bind(handler);
        return catchAsyncResult(fn(event as TracingEvent), handler.name, 'tracing', logger);
      }

      case 'log':
        if (handler.onLogEvent) {
          return catchAsyncResult(handler.onLogEvent(event as LogEvent), handler.name, 'log', logger);
        }
        break;

      case 'metric':
        if (handler.onMetricEvent) {
          return catchAsyncResult(handler.onMetricEvent(event as MetricEvent), handler.name, 'metric', logger);
        }
        break;

      case 'score':
        if (handler.onScoreEvent) {
          return catchAsyncResult(handler.onScoreEvent(event as ScoreEvent), handler.name, 'score', logger);
        }
        if ((handler as LegacyScoreHandler).addScoreToTrace) {
          const score = (event as ScoreEvent).score;
          if (!score.traceId) break;
          return catchAsyncResult(
            (handler as LegacyScoreHandler).addScoreToTrace!({
              traceId: score.traceId,
              ...(score.spanId ? { spanId: score.spanId } : {}),
              score: score.score,
              ...(score.reason ? { reason: score.reason } : {}),
              scorerName: score.scorerName ?? score.scorerId,
              ...(score.metadata ? { metadata: score.metadata as Record<string, any> } : {}),
            }),
            handler.name,
            'score',
            logger,
          );
        }
        break;

      case 'feedback':
        if (handler.onFeedbackEvent) {
          return catchAsyncResult(handler.onFeedbackEvent(event as FeedbackEvent), handler.name, 'feedback', logger);
        }
        break;
    }
  } catch (err) {
    logger.error(`[Observability] Handler error [handler=${handler.name}]:`, err);
  }
}

/**
 * Route exporter pipeline drop events to a handler.
 *
 * Drop events are meta-events for alerting and health reporting, so they use a
 * dedicated hook instead of the normal observability signal router.
 */
export function routeDropToHandler(
  handler: ObservabilityHandler,
  event: ObservabilityDropEvent,
  logger: IMastraLogger,
): void | Promise<void> {
  try {
    if (handler.onDroppedEvent) {
      return catchAsyncResult(handler.onDroppedEvent(event), handler.name, 'drop', logger);
    }
  } catch (err) {
    logger.error(`[Observability] Handler error [handler=${handler.name}]:`, err);
  }
}

/**
 * Catch rejected promises from async handlers and return the tracked promise.
 * The returned promise always resolves (never rejects) — errors are logged.
 */
function catchAsyncResult(
  result: void | Promise<void>,
  handlerName: string,
  signal: string,
  logger: IMastraLogger,
): void | Promise<void> {
  if (result && typeof (result as Promise<void>).then === 'function') {
    return (result as Promise<void>).catch(err => {
      logger.error(`[Observability] ${signal} handler error [handler=${handlerName}]:`, err);
    });
  }
}
